import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ProjectTagDefinition } from "@/zapi-frontend/pages/CreateProjectPage/ui/blocks/ProjectDetails";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { GET_PROJECT_DIMENSIONS_KEY } from "@/zapi-frontend/shared/services/useProjectDimensions";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { GET_SAVED_FILTERS_KEY } from "@/zapi-frontend/shared/services/useSavedFilters";
import { DIMENSION_PRESET_SWATCHES } from "@/zapi-frontend/pages/ProjectsPage/const/const";
import { useCreateProjectDimension } from "@/zapi-frontend/pages/ProjectsPage/services/useCreateProjectDimension";
import { useUpdateProjectDimension } from "@/zapi-frontend/pages/ProjectsPage/services/useUpdateProjectDimension";
import { useDeleteProjectDimension } from "@/zapi-frontend/pages/ProjectsPage/services/useDeleteProjectDimension";

type TTagDefinition = Omit<ProjectTagDefinition, "id">;

// The organization's tags are its project dimensions; the card manages them the way the project list does.
export function useOrganizationTags({
  organizationId,
  dimensions,
  values,
  onValueCleared
}: {
  organizationId: number;
  dimensions: TOrganizationProjectDimension[];
  values: Record<string, string>;
  onValueCleared: (dimensionId: number) => void;
}) {
  const { t } = useTranslation("createProject");
  const queryClient = useQueryClient();
  const queryKey = [GET_PROJECT_DIMENSIONS_KEY, organizationId];
  const refreshProjects = () => {
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY, organizationId] });
    queryClient.invalidateQueries({ queryKey: [GET_SAVED_FILTERS_KEY, organizationId] });
  };

  const create = useCreateProjectDimension({
    onSuccess: (created) => {
      queryClient.setQueryData<TOrganizationProjectDimension[]>(queryKey, (old) => [...(old ?? []), created]);
      refreshProjects();
    },
    onError: (error) => toast.error(error.response?.data?.message || t("newProject.tags.saveFailed"))
  });

  const update = useUpdateProjectDimension({
    onSuccess: (updated) => {
      queryClient.setQueryData<TOrganizationProjectDimension[]>(queryKey, (old) =>
        (old ?? []).map((dimension) => (dimension.id === updated.id ? updated : dimension))
      );
      // A removed option takes the tag off this draft too, as it does on saved projects.
      const value = values[String(updated.id)];
      if (updated.type === "dropdown" && value && !updated.options.includes(value)) onValueCleared(updated.id);
      refreshProjects();
    },
    onError: (error) => toast.error(error.response?.data?.message || t("newProject.tags.saveFailed"))
  });

  const remove = useDeleteProjectDimension({
    onSuccess: (dimensionId) => {
      queryClient.setQueryData<TOrganizationProjectDimension[]>(queryKey, (old) =>
        (old ?? []).filter((dimension) => dimension.id !== dimensionId)
      );
      if (values[String(dimensionId)]) onValueCleared(dimensionId);
      refreshProjects();
    },
    onError: (error) => toast.error(error.response?.data?.message || t("newProject.tags.deleteFailed"))
  });

  return {
    onCreate: (definition: TTagDefinition) =>
      create.mutate({
        orgId: organizationId,
        name: definition.name,
        type: definition.type,
        options: definition.options ?? [],
        color: DIMENSION_PRESET_SWATCHES[dimensions.length % DIMENSION_PRESET_SWATCHES.length]
      }),
    onUpdate: (definitionId: string, definition: TTagDefinition) => {
      const existing = dimensions.find((dimension) => String(dimension.id) === definitionId);
      if (!existing) return;
      // The edit form already tells the user that removing an option takes it off the projects that picked it.
      update.mutate({
        orgId: organizationId,
        dimensionId: existing.id,
        name: definition.name,
        type: existing.type,
        options: definition.options ?? [],
        color: existing.color || DIMENSION_PRESET_SWATCHES[0],
        confirmDestructive: true
      });
    },
    onDelete: (definitionId: string) => remove.mutate({ orgId: organizationId, dimensionId: Number(definitionId) })
  };
}
